import {Typography, Box, Button} from '@mui/material';
import {CustomSwitch} from './planCard';
import {mainLinearGradient} from '../../theme/gradients';

/**
 * HeaderButton component for rendering a button in the plan header.
 *
 * @component
 * @param {Object} props - The properties of the component.
 * @param {Function} props.onClick - The function to be called on button click.
 * @param {boolean} props.isCurrentUserPlan - Indicates whether the current plan belongs to the user.
 * @param {string} props.buttonText - The text content of the button.
 * @returns {JSX.Element} The HeaderButton component.
 */
export const HeaderButton = ({onClick, isCurrentUserPlan, buttonText}) => {
  return (
    <Button
      sx={{
        borderRadius: 6,
        border: '3px solid transparent',
        ...(!isCurrentUserPlan && {
          background: ` linear-gradient(white,white) padding-box,${mainLinearGradient} border-box`,
        }),
      }}
      onClick={isCurrentUserPlan ? () => {} : onClick}
    >
      <Typography
        sx={{
          background: mainLinearGradient,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontSize: 17,
          fontWeight: 700,
        }}
      >
        {buttonText}
      </Typography>
    </Button>
  );
};

/**
 * BasicPlanHeader component for rendering the header of the basic plan.
 *
 * @component
 * @param {Object} props - The properties of the component.
 * @param {boolean} props.isCurrentUserPlan - Indicates whether the current plan belongs to the user.
 * @param {Function} props.onClick - The function to be called on button click.
 * @returns {JSX.Element} The BasicPlanHeader component.
 */
export const BasicPlanHeader = ({isCurrentUserPlan, onClick}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        height: '100%',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Typography fontSize="26px" fontWeight={700}>
          Basic
        </Typography>
        <Typography fontSize="40px" fontWeight={700}>
          $0
          {/* the margin from this typography component is to maintain the same height as the premium header  */}
          <span
            style={{
              fontSize: '20px',
              fontWeight: 400,
            }}
          >
            {' '}
            /mo
          </span>
        </Typography>
      </Box>
      <HeaderButton
        onClick={onClick}
        isCurrentUserPlan={isCurrentUserPlan}
        buttonText={isCurrentUserPlan ? 'You are on this plan' : 'Go back to basic plan'}
      />
    </Box>
  );
};

/**
 * PremiumPlanHeader component for rendering the header of the premium plan.
 *
 * @component
 * @param {Object} props - The properties of the component.
 * @param {boolean} props.switchValue - The value of the CustomSwitch component.
 * @param {Function} props.switchOnChange - The function to be called on switch change.
 * @param {string} props.price - The price of the premium plan.
 * @param {boolean} props.isCurrentUserPlan - Indicates whether the current plan belongs to the user.
 * @param {Function} props.onClick - The function to be called on button click.
 * @param {boolean} props.disabled - Indicates whether the plan switch is disabled.
 * @returns {JSX.Element} The PremiumPlanHeader component.
 */
export const PremiumPlanHeader = ({switchValue, switchOnChange, price, isCurrentUserPlan, onClick, disabled}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
      }}
    >
      <Typography fontSize="26px" fontWeight={700}>
        Premium
      </Typography>
      <Typography fontSize="40px" fontWeight={700}>
        ${price}
        <span
          style={{
            fontSize: '20px',
            fontWeight: 400,
          }}
        >
          {' '}
          /mo
        </span>
      </Typography>
      {switchValue && (
        <Typography fontSize="20px" fontWeight={700}>
          $384
          <span
            style={{
              fontSize: '14px',
              fontWeight: 400,
            }}
          >
            {' '}
            /yr billed annually
          </span>
        </Typography>
      )}
      <Box sx={{display: 'flex', justifyContent: 'center', gap: 1, my: 1}}>
        <Typography>monthly</Typography>
        <CustomSwitch onChange={switchOnChange} checked={switchValue} disabled={disabled} />
        <Box>
          <Typography>anually</Typography>
          <Typography color="#31A1F3" fontWeight={700}>
            save 27%
          </Typography>
        </Box>
      </Box>
      <HeaderButton
        onClick={onClick}
        isCurrentUserPlan={isCurrentUserPlan}
        buttonText={isCurrentUserPlan ? 'You are on this plan' : 'Upgrade to premium'}
      />
    </Box>
  );
};
